import { css } from 'styled-components';
import {
    ColorNeutralBlack,
    ColorNeutralWhite,
    ColorOpacityDark1,
    ColorOpacityDark2,
    ColorOpacityLight1,
    ColorOpacityLight2
} from 'autogen/design-tokens/tokens';
import type InlineButtonVariation from './types/inlineButtonVariation';

const sharedDisableStyling = css`
    cursor: not-allowed;
`;

const sharedFocusStyling = css`
    outline-style: solid;
    outline-width: 2px;
    outline-offset: 1px;
`;

const inherit: InlineButtonVariation = css<{ $disabled: boolean }>`
    color: currentColor;

    ${({ $disabled }) =>
        $disabled &&
        css`
            ${sharedDisableStyling}
        `};
`;

const primary: InlineButtonVariation = css<{ $disabled: boolean }>`
    color: ${({ theme }) => theme.ComponentsCtaInlineText};

    ${({ $disabled }) =>
        $disabled
            ? css`
                ${sharedDisableStyling}
                color: ${({ theme }) => theme.ComponentsCtaInlineTextDisabled};
              `
            : css`
                  &:hover {
                    color: ${({ theme }) => theme.ComponentsCtaInlineTextHover};
                  }

                  &:focus {
                    ${sharedFocusStyling}
                    outline-color: ${({ theme }) => theme.ComponentsCtaInlineBorderFocus};
                    color: ${({ theme }) => theme.ComponentsCtaInlineTextFocus};
                  }
              `}
`;

const secondary: InlineButtonVariation = css<{ $disabled: boolean }>`
    color: ${({ theme }) => theme.ComponentsCtaInlineSecondaryText};

    ${({ $disabled }) =>
        $disabled
            ? css`
                ${sharedDisableStyling}
                color: ${({ theme }) => theme.ComponentsCtaInlineSecondaryTextDisabled};
              `
            : css`
                  &:hover {
                    color: ${({ theme }) => theme.ComponentsCtaInlineSecondaryTextHover};
                  }

                  &:focus {
                    ${sharedFocusStyling}
                    outline-color: ${({ theme }) => theme.ComponentsCtaInlineSecondaryBorderFocus};
                    color: ${({ theme }) => theme.ComponentsCtaInlineSecondaryTextFocus};
                  }
              `}
`;

const primaryInverted: InlineButtonVariation = css<{ $disabled: boolean }>`
    color: ${({ theme }) => theme.ComponentsCtaInlineInvertedText};

    ${({ $disabled }) =>
        $disabled
            ? css`
                ${sharedDisableStyling}
                color: ${({ theme }) => theme.ComponentsCtaInlineInvertedTextDisabled};
              `
            : css`
                  &:hover {
                    color: ${({ theme }) => theme.ComponentsCtaInlineInvertedTextHover};
                  }

                  &:focus {
                    ${sharedFocusStyling}
                    outline-color: ${({ theme }) => theme.ComponentsCtaInlineInvertedBorderFocus};
                    color: ${({ theme }) => theme.ComponentsCtaInlineInvertedTextFocus};
                  }
              `}
`;

const dark: InlineButtonVariation = css<{ $disabled: boolean }>`
    color: ${ColorNeutralBlack};

    ${({ $disabled }) =>
        $disabled
            ? css`
                  ${sharedDisableStyling}
              `
            : css`
                  &:hover {
                      color: ${ColorOpacityDark2};
                  }

                  &:focus {
                      color: ${ColorOpacityDark1};
                  }
              `}
`;

const light: InlineButtonVariation = css<{ $disabled: boolean }>`
    color: ${ColorNeutralWhite};

    ${({ $disabled }) =>
        $disabled
            ? css`
                  ${sharedDisableStyling}
              `
            : css`
                  &:hover {
                      color: ${ColorOpacityLight2};
                  }

                  &:focus {
                      color: ${ColorOpacityLight1};
                  }
              `}
`;

const deprecatedVariants = {
    inherit,
    dark,
    light
} as const;

export const inlineButtonVariants = {
    primary,
    secondary,
    primaryInverted,
    ...deprecatedVariants
} as const;

export type inlineButtonVariantKeys = keyof typeof inlineButtonVariants;

export const variantStyling = css<{ $variant?: inlineButtonVariantKeys }>`
    ${({ $variant }) => ($variant ? inlineButtonVariants[$variant] : null)}
`;

export default inlineButtonVariants;
